/**
 * KycIdenfy — сбор KYC через redirect-флоу iDenfy (data_source=idenfy).
 * Клиент уходит на хостинг iDenfy; результат приходит вебхуком. После одобрения добираем
 * недостающее: селфи (если провайдер не вернул лицо) и поля выдачи паспорта («кем выдан» — DaData ФМС).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { openExternal } from "@/telegram/tg";
import { Card, FallbackButton, Screen } from "@/components/Screen";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { Field } from "@/components/Field";
import { CameraCapture } from "@/components/CameraCapture";
import { friendlyError, kycApi, type KycStatus } from "@/lib/api";
import {
  ApprovedCard,
  AwaitingFinalCard,
  ErrorCard,
  PhotoSourceButtons,
  RejectedCard,
  SkeletonBlock,
  SubmittedCard,
  resolveOutcome,
} from "@/screens/onboarding/kyc/shared";

type Props = { onNext: (status: KycStatus) => void; onBack: () => void };

type IdenfyStage =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; redirectUrl: string }
  | { kind: "submitted"; latest?: KycStatus }
  | { kind: "selfie"; latest?: KycStatus }
  | { kind: "issue_details"; latest?: KycStatus }
  | { kind: "awaiting"; latest?: KycStatus }
  | { kind: "approved"; latest: KycStatus }
  | { kind: "rejected"; latest: KycStatus };

// После одобрения iDenfy собираем недостающее: сперва селфи (если провайдер не вернул
// фото лица), затем поля выдачи паспорта. Когда всё есть — ждём финал брокера.
function awaitingStage(s: KycStatus): IdenfyStage {
  if (s.needs_selfie) return { kind: "selfie", latest: s };
  if (s.needs_issue_details) return { kind: "issue_details", latest: s };
  return { kind: "awaiting", latest: s };
}

export function KycIdenfy({ onNext, onBack }: Props) {
  const [stage, setStage] = useState<IdenfyStage>({ kind: "loading" });
  const pollRef = useRef<number | null>(null);

  const createSession = useCallback(async (): Promise<string> => {
    const { redirect_url } = await kycApi.idenfySession();
    return redirect_url;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const status = await kycApi.status();
        const outcome = resolveOutcome(status);
        if (outcome === "approved") {
          setStage({ kind: "approved", latest: status });
          return;
        }
        if (outcome === "rejected") {
          setStage({ kind: "rejected", latest: status });
          return;
        }
        if (outcome === "awaiting") {
          // iDenfy уже подтвердил — не создаём новую сессию.
          // Сначала добираем недостающее (селфи, поля выдачи паспорта), потом ждём брокера.
          const st = awaitingStage(status);
          setStage(st);
          if (st.kind === "awaiting") startPolling();
          return;
        }
        const redirectUrl = await createSession();
        setStage({ kind: "ready", redirectUrl });
      } catch (e) {
        setStage({ kind: "error", message: friendlyError(e) });
      }
    })();
  }, [createSession]);

  useEffect(() => {
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const s = await kycApi.status();
        const outcome = resolveOutcome(s);
        if (outcome === "approved") {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setStage({ kind: "approved", latest: s });
        } else if (outcome === "rejected") {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setStage({ kind: "rejected", latest: s });
        } else if (outcome === "awaiting") {
          if (s.needs_selfie || s.needs_issue_details) {
            // нужно добрать данные (селфи/поля выдачи) — останавливаем опрос до отправки формы
            if (pollRef.current) window.clearInterval(pollRef.current);
            pollRef.current = null;
            setStage(awaitingStage(s));
          } else {
            setStage({ kind: "awaiting", latest: s });
          }
        } else {
          setStage((cur) => (cur.kind === "submitted" ? { kind: "submitted", latest: s } : cur));
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
  }, []);

  const launch = useCallback(
    (url: string) => {
      openExternal(url);
      setStage({ kind: "submitted" });
      startPolling();
    },
    [startPolling],
  );

  const proceed = useCallback(() => {
    if (stage.kind === "approved") onNext(stage.latest);
  }, [stage, onNext]);

  useBackButton(onBack);
  useMainButton({
    text: stage.kind === "approved" ? "Продолжить" : "Начать верификацию",
    onClick: () => {
      if (stage.kind === "approved") proceed();
      else if (stage.kind === "ready") launch(stage.redirectUrl);
    },
    visible: stage.kind === "approved" || stage.kind === "ready",
    active: stage.kind === "approved" || stage.kind === "ready",
  });

  return (
    <Screen
      title={
        stage.kind === "approved"
          ? "Верификация пройдена ✓"
          : stage.kind === "rejected"
          ? "Верификация не пройдена"
          : stage.kind === "awaiting"
          ? "Первичная проверка пройдена ✓"
          : stage.kind === "issue_details"
          ? "Ещё пара полей паспорта"
          : stage.kind === "selfie"
          ? "Нужно фото лица"
          : stage.kind === "submitted"
          ? "Проверяем документы"
          : "Верификация личности"
      }
      subtitle={
        stage.kind === "ready"
          ? "Откроется защищённая страница iDenfy. Сфотографируйте паспорт и сделайте видео-селфи — это нужно один раз. После завершения вернитесь в приложение."
          : stage.kind === "issue_details"
          ? "Личность подтверждена. Укажите дату выдачи и код подразделения паспорта — этих данных нет на отсканированной странице, а они нужны для оформления."
          : stage.kind === "selfie"
          ? "Личность подтверждена. Сделайте селфи или загрузите фото лица — это нужно для оформления."
          : stage.kind === "submitted"
          ? "Ожидаем результата проверки. Обычно занимает несколько минут — окно можно не закрывать."
          : undefined
      }
    >
      {stage.kind === "loading" && <SkeletonBlock />}

      {stage.kind === "error" && <ErrorCard message={stage.message} />}

      {stage.kind === "ready" && (
        <Button onClick={() => launch(stage.redirectUrl)}>Начать верификацию</Button>
      )}

      {stage.kind === "submitted" && <SubmittedCard text="iDenfy проверяет документы…" />}

      {stage.kind === "selfie" && (
        <SelfieForm
          onSubmitted={async () => {
            // после селфи могут остаться поля выдачи — переспрашиваем статус и роутим
            try {
              const s = await kycApi.status();
              const st = awaitingStage(s);
              setStage(st);
              if (st.kind === "awaiting") startPolling();
            } catch {
              setStage({ kind: "awaiting" });
              startPolling();
            }
          }}
        />
      )}

      {stage.kind === "issue_details" && (
        <IssueDetailsForm
          onSubmitted={() => {
            setStage({ kind: "awaiting" });
            startPolling();
          }}
        />
      )}

      {stage.kind === "awaiting" && <AwaitingFinalCard />}

      {stage.kind === "approved" && <ApprovedCard />}

      {stage.kind === "rejected" && (
        <RejectedCard
          latest={stage.latest}
          onRetry={async () => {
            try {
              const redirectUrl = await createSession();
              setStage({ kind: "ready", redirectUrl });
            } catch (e) {
              setStage({ kind: "error", message: friendlyError(e) });
            }
          }}
        />
      )}

      {stage.kind === "approved" && <FallbackButton text="Продолжить" onClick={proceed} />}
    </Screen>
  );
}

// Загрузка селфи для iDenfy-клиента, когда провайдер не вернул фото лица (liveness в
// кабинете отключена). Без распознавания — снимок уходит брокеру как фото лица.
function SelfieForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      await kycApi.idenfySelfie(file);
      setDone(true);
      onSubmitted();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {done && !error ? (
        <Note variant="success">✓ Фото принято</Note>
      ) : (
        <PhotoSourceButtons busy={busy} onCamera={() => setCameraOpen(true)} onFile={upload} />
      )}

      {busy && <div className="text-[13px] text-white/45">Загрузка…</div>}
      {error && <ErrorCard message={error} />}

      {cameraOpen && (
        <CameraCapture
          facing="user"
          onClose={() => setCameraOpen(false)}
          onCapture={(f) => {
            setCameraOpen(false);
            upload(f);
          }}
        />
      )}
    </div>
  );
}

// Добор полей выдачи паспорта для iDenfy-клиентов (iDenfy не отдаёт нижнюю часть разворота).
// «Кем выдан» подтягиваем из справочника ФМС DaData по коду подразделения.
function IssueDetailsForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [issuedAt, setIssuedAt] = useState("");
  const [code, setCode] = useState("");
  const [series, setSeries] = useState("");
  const [fmsName, setFmsName] = useState<string | null>(null);
  const [lookupDone, setLookupDone] = useState(false);
  const [manualIssuedBy, setManualIssuedBy] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeDigits = code.replace(/\D/g, "");
  const seriesDigits = series.replace(/\D/g, "");

  useEffect(() => {
    if (codeDigits.length !== 6) {
      setFmsName(null);
      setLookupDone(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { items } = await kycApi.fmsUnit(code);
        if (!cancelled) {
          setFmsName(items[0]?.name ?? null);
          setLookupDone(true);
        }
      } catch {
        if (!cancelled) {
          setFmsName(null);
          setLookupDone(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, codeDigits.length]);

  const issuedBy = fmsName ?? manualIssuedBy.trim();
  const canSubmit =
    !!issuedAt && seriesDigits.length === 4 && codeDigits.length === 6 && !!issuedBy && !saving;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await kycApi.issueDetails({
        issued_at: issuedAt,
        division_code: code,
        issued_by: issuedBy || undefined,
        passport_series: series,
      });
      onSubmitted();
    } catch (e) {
      setError(friendlyError(e));
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Field
        label="Серия паспорта"
        inputMode="numeric"
        placeholder="4 цифры"
        value={series}
        onChange={(e) => setSeries(e.target.value)}
        error={series && seriesDigits.length !== 4 ? "Серия — 4 цифры" : undefined}
      />
      <Field label="Дата выдачи" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
      <Field
        label="Код подразделения"
        inputMode="numeric"
        placeholder="NNN-NNN"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        error={code && codeDigits.length !== 6 ? "Код — 6 цифр (NNN-NNN)" : undefined}
      />

      {codeDigits.length === 6 && fmsName && (
        <Card>
          <span className="text-white/45">Кем выдан: </span>
          {fmsName}
        </Card>
      )}
      {codeDigits.length === 6 && lookupDone && !fmsName && (
        <Field
          label="Кем выдан (по коду не нашли — впишите вручную)"
          value={manualIssuedBy}
          onChange={(e) => setManualIssuedBy(e.target.value)}
        />
      )}

      {error && <ErrorCard message={error} />}

      <Button onClick={submit} disabled={!canSubmit}>
        {saving ? "Сохранение…" : "Готово"}
      </Button>
    </div>
  );
}
